import Vue from 'vue'
import { Component } from 'vue-property-decorator'
import { MasterTemplate } from '@appbase/templates'
import GnbLeftView from '@gnb/views/GnbLeft/GnbLeftView.vue'
import boardApi from '../api/board'
import menuHelper from '@appbase/util/menu-helper'
import { dispatchEventBus } from '@appbase/util'

@Component({
    components: {
        MasterTemplate,
        GnbLeftView
    }
})
export default class BoardMaster extends Vue {

    async mounted() {
        // 게시글 작성 완료 이벤트 리스너 등록
        this.$root.$on('article-created', this.onArticleCreated);

        // localStorage 이벤트 리스너 등록 (크로스 윈도우 통신)
        window.addEventListener('storage', this.onStorageChange);

        // DOM이 완전히 렌더링될 때까지 대기 후 신규 글 배지 표시
        this.$nextTick(() => {
            setTimeout(async () => {
                await this.addNewArticleBadges();
            }, 500);
        });
    }

    beforeDestroy() {
        // 이벤트 리스너 해제
        this.$root.$off('article-created', this.onArticleCreated);
        window.removeEventListener('storage', this.onStorageChange);
    }

    onStorageChange(event: StorageEvent) {
        console.log('[BoardMaster] Storage event:', event);

        if (event.key === 'board-event' && event.newValue) {
            try {
                const eventData = JSON.parse(event.newValue);
                console.log('[BoardMaster] Parsed event data:', eventData);

                if (eventData.type === 'article-created' && eventData.boardId) {
                    console.log('[BoardMaster] Article created event from localStorage, boardId:', eventData.boardId);
                    this.onArticleCreated(eventData.boardId);
                } else if (eventData.type === 'article-deleted' && eventData.boardId) {
                    console.log('[BoardMaster] Article deleted event from localStorage, boardId:', eventData.boardId);
                    this.onArticleDeleted(eventData.boardId);
                }
            } catch (err) {
                console.error('[BoardMaster] Error parsing storage event:', err);
            }
        }
    }

    async onArticleDeleted(boardId: number) {
        console.log(`[BoardMaster] onArticleDeleted called with boardId: ${boardId}`);

        // 게시글이 삭제된 게시판의 신규 글 여부 재확인
        try {
            const hasNew = await boardApi.hasNewArticles(boardId);
            console.log(`[BoardMaster] After deletion, hasNew = ${hasNew}`);

            const currentMenuId = Number(this.$route.query.MENUID);
            if (!currentMenuId) return;

            const leftMenu = menuHelper.getLeftMenuList(currentMenuId);
            if (!leftMenu) return;

            // 메뉴 트리에서 해당 boardId를 가진 메뉴 찾기
            const targetMenu = this.findMenuByBoardId(leftMenu, boardId);
            if (targetMenu) {
                console.log(`[BoardMaster] Updating badge for: ${targetMenu.menuName}, hasNew=${hasNew}`);
                // 메뉴 이름으로 배지 업데이트 (trim으로 공백 제거)
                this.updateMenuBadgeByName(targetMenu.menuName.trim(), hasNew);
            }
        } catch (error) {
            console.error(`Failed to check new articles after deletion:`, error);
        }
    }

    async onArticleCreated(boardId: number) {
        console.log(`[BoardMaster] onArticleCreated called with boardId: ${boardId}`);

        // 게시글이 작성된 게시판의 배지 갱신
        const currentMenuId = Number(this.$route.query.MENUID);
        if (!currentMenuId) {
            console.log('[BoardMaster] No currentMenuId');
            return;
        }

        const leftMenu = menuHelper.getLeftMenuList(currentMenuId);
        if (!leftMenu) {
            console.log('[BoardMaster] No leftMenu');
            return;
        }

        // 메뉴 트리에서 해당 boardId를 가진 메뉴 찾기
        const targetMenu = this.findMenuByBoardId(leftMenu, boardId);
        console.log(`[BoardMaster] targetMenu found:`, targetMenu);

        if (targetMenu) {
            console.log(`[BoardMaster] Updating badge for: ${targetMenu.menuName}`);
            // 메뉴 이름으로 배지 업데이트 (trim으로 공백 제거)
            this.updateMenuBadgeByName(targetMenu.menuName.trim(), true);
        } else {
            console.log(`[BoardMaster] targetMenu not found for boardId: ${boardId}`);
        }
    }

    findMenuByBoardId(menu: appbase.cache.TMenuItem, boardId: number): appbase.cache.TMenuItem | null {
        // 현재 메뉴가 해당 게시판인 경우
        if (menu.menuType === 'BB' && menu.systemId === boardId) {
            return menu;
        }

        // 하위 메뉴에서 재귀적으로 찾기
        if (menu.children && menu.children.length > 0) {
            for (const child of menu.children) {
                const found = this.findMenuByBoardId(child, boardId);
                if (found) return found;
            }
        }

        return null;
    }

    async addNewArticleBadges() {
        console.log('[BoardMaster] addNewArticleBadges started');

        // menuHelper에서 메뉴 정보 가져오기
        const currentMenuId = Number(this.$route.query.MENUID);
        const leftMenuRoot = menuHelper.getLeftMenuList(currentMenuId);

        console.log(`[BoardMaster] currentMenuId: ${currentMenuId}, leftMenuRoot:`, leftMenuRoot);

        if (!leftMenuRoot) {
            console.log('[BoardMaster] No leftMenuRoot');
            return;
        }

        // 메뉴 트리에서 모든 게시판 찾기
        const allBoardMenus: appbase.cache.TMenuItem[] = [];
        this.collectBoardMenus(leftMenuRoot, allBoardMenus);

        console.log(`[BoardMaster] Found ${allBoardMenus.length} board menus`);

        // 각 게시판 메뉴에 대해 신규 글 체크
        for (const menuItem of allBoardMenus) {
            if (menuItem.menuType === 'BB' && menuItem.systemId > 0) {
                try {
                    const hasNew = await boardApi.hasNewArticles(menuItem.systemId);
                    console.log(`[BoardMaster] ${menuItem.menuName} (boardId: ${menuItem.systemId}): hasNew = ${hasNew}`);
                    // DOM에서 해당 메뉴 노드 찾아서 배지 업데이트 (trim으로 공백 제거)
                    this.updateMenuBadgeByName(menuItem.menuName.trim(), hasNew);
                } catch (error) {
                    console.error(`Failed to check new articles for ${menuItem.menuName}:`, error);
                }
            }
        }

        console.log('[BoardMaster] addNewArticleBadges finished');
    }

    collectBoardMenus(menu: appbase.cache.TMenuItem, result: appbase.cache.TMenuItem[]) {
        // 현재 메뉴가 게시판이면 추가
        if (menu.menuType === 'BB') {
            result.push(menu);
        }

        // 하위 메뉴 재귀 탐색
        if (menu.children && menu.children.length > 0) {
            for (const child of menu.children) {
                this.collectBoardMenus(child, result);
            }
        }
    }

    updateMenuBadgeByName(menuName: string, hasNew: boolean) {
        console.log(`[BoardMaster] updateMenuBadgeByName: ${menuName}, hasNew=${hasNew}`);

        // DOM에서 메뉴 이름으로 노드 찾기
        const allNodes = document.querySelectorAll('.mn-node');
        console.log(`[BoardMaster] Total nodes: ${allNodes.length}`);

        for (const node of Array.from(allNodes)) {
            const nameEl = node.querySelector('.menu-name a') as HTMLElement;
            if (nameEl && nameEl.textContent && nameEl.textContent.trim() === menuName) {
                console.log(`[BoardMaster] Found node for ${menuName}, calling updateMenuBadgeByNode`);
                this.updateMenuBadgeByNode(node as Element, hasNew);
                return;
            }
        }
        console.log(`[BoardMaster] Node not found for ${menuName}`);
    }

    updateMenuBadgeByNode(menuNode: Element, hasNew: boolean) {
        console.log(`[BoardMaster] updateMenuBadgeByNode called, hasNew=${hasNew}`);

        // 메뉴명 요소 찾기
        const menuNameSpan = menuNode.querySelector('.menu-name') as HTMLElement;
        if (!menuNameSpan) {
            console.log('[BoardMaster] menuNameSpan not found');
            return;
        }

        console.log('[BoardMaster] menuNameSpan found:', menuNameSpan);

        // 기존 N 배지 제거
        const existingBadge = menuNameSpan.querySelector('.new');
        if (existingBadge) {
            console.log('[BoardMaster] Removing existing badge');
            existingBadge.remove();
        }

        if (hasNew) {
            // 게시판명 바로 우측에 N 배지 추가
            const badge = document.createElement('span');
            badge.className = 'new';
            badge.textContent = 'N';
            badge.style.marginLeft = '5px';
            console.log('[BoardMaster] Appending badge:', badge);
            menuNameSpan.appendChild(badge);
            console.log('[BoardMaster] Badge appended, menuNameSpan.innerHTML:', menuNameSpan.innerHTML);
        }
    }
}
